// Time on a video, written and read the way people say it on a call.
//
// A comment on a video is anchored to a moment (and optionally a range), and
// the studio and the client both refer to it by its timecode: "at 0:12".
// Formatting, parsing and frame stepping live here so the player, the comment
// list and the scrubber markers cannot disagree about where 0:12 is.

export const DEFAULT_FPS = 24

const num = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const pad = (n, width = 2) => String(n).padStart(width, '0')

/** Whole-second timecode: 0:04, 1:02:09. Negative or missing reads as 0:00. */
export function formatTimecode(seconds) {
  const s = Math.max(0, Math.floor(num(seconds) ?? 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`
}

/**
 * Timecode with hundredths: 0:12.08. What the composer shows, because "the
 * frame I paused on" is finer than a second.
 */
export function formatPreciseTimecode(seconds) {
  const raw = Math.max(0, num(seconds) ?? 0)
  // Round to hundredths first, so 12.999 becomes 0:13.00 rather than 0:12.100.
  const hundredths = Math.round(raw * 100)
  const whole = Math.floor(hundredths / 100)
  return `${formatTimecode(whole)}.${pad(hundredths % 100)}`
}

/** A range as people write one: 0:04–0:09. A missing or backwards end is just the start. */
export function formatRange(start, end) {
  const a = num(start)
  const b = num(end)
  if (a == null) return ''
  if (b == null || b <= a) return formatTimecode(a)
  return `${formatTimecode(a)}–${formatTimecode(b)}`
}

/**
 * Read a typed timecode back into seconds.
 *
 * Accepts "12", "12.5", "0:12", "0:12.08", "1:02:09". Anything else — a
 * minute or second field of 60 or more, letters, a negative — is null, so a
 * mistyped time is refused rather than quietly landing somewhere else.
 */
export function parseTimecode(text) {
  const raw = String(text ?? '').trim()
  if (!raw) return null
  if (!/^\d+(:\d{1,2}){0,2}(\.\d+)?$/.test(raw)) return null
  const [main, frac] = raw.split('.')
  const parts = main.split(':').map(Number)
  if (parts.length > 1 && parts.slice(1).some(p => p >= 60)) return null
  const whole = parts.reduce((acc, p) => acc * 60 + p, 0)
  return frac ? whole + Number(`0.${frac}`) : whole
}

/** One frame, in seconds. A missing or silly fps falls back to 24. */
export function frameDuration(fps) {
  const f = num(fps)
  return 1 / (f && f > 0 && f <= 240 ? f : DEFAULT_FPS)
}

export function clampTime(t, duration) {
  const x = Math.max(0, num(t) ?? 0)
  const d = num(duration)
  return d != null && d > 0 ? Math.min(x, d) : x
}

/**
 * One frame forward or back, landing on a frame boundary rather than drifting
 * by a float's worth each press.
 */
export function stepFrame(current, fps, direction = 1, duration = null) {
  const step = frameDuration(fps)
  const frame = Math.round((num(current) ?? 0) / step)
  const next = (frame + (direction < 0 ? -1 : 1)) * step
  return clampTime(Math.round(next * 1000) / 1000, duration)
}

/** Where a marker sits on the scrubber, as a percentage clamped to 0–100. */
export function markerPercent(time, duration) {
  const t = num(time)
  const d = num(duration)
  if (t == null || !d || d <= 0) return 0
  return Math.min(100, Math.max(0, (t / d) * 100))
}

export const PLAYBACK_RATES = [0.25, 0.5, 1, 1.5, 2]

/** The next speed in the cycle the speed button steps through. */
export function nextPlaybackRate(rate) {
  const i = PLAYBACK_RATES.indexOf(num(rate))
  if (i < 0) return 1
  return PLAYBACK_RATES[(i + 1) % PLAYBACK_RATES.length]
}
